// /api/activity — the live who-is-running-what view over the single-threaded
// Pacesetter daemon, and its anonymized /busy variant used by the shared error
// card. See internal/server/activity.go.

export interface ActivityRow {
  user: string;
  verb: string;
  params?: string;
  started_at: string;
  running_ms?: number; // in-flight rows
  ms?: number; // recent rows
  ok?: boolean; // recent rows
}

export interface ActivitySnapshot {
  in_flight: ActivityRow[];
  recent: ActivityRow[];
}

export interface BusySignal {
  busy: boolean;
  verb?: string;
  running_ms?: number;
}

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

export function fetchActivity(): Promise<ActivitySnapshot> {
  return getJSON<ActivitySnapshot>('/api/activity');
}

export function fetchBusy(): Promise<BusySignal> {
  return getJSON<BusySignal>('/api/activity/busy');
}

/** Friendly Spanish names for daemon verbs; raw verb when unmapped. */
const VERB_LABELS: Record<string, string> = {
  sales: 'Ventas (rango de fechas)',
  'sales-period': 'Gráfica de ventas por periodo',
  'sales-lines': 'Reporte de ventas por partida',
  'counter-lines': 'Reporte de ventas de mostrador por línea (comisiones)',
  'agent-sales': 'Reporte de ventas por agente y sucursal (comisiones)',
  'sales-returns': 'Devoluciones de venta',
  'lost-sales': 'Ventas perdidas',
  ar: 'Cartera (CXC)',
  statement: 'Estado de cuenta',
  ticket: 'Detalle de ticket',
  quotes: 'Cotizaciones (libro abierto)',
  'quote-detail': 'Detalle de cotización',
  parts: 'Búsqueda de partes',
  'part-detail': 'Detalle de pieza',
  'part-filters': 'Filtros de partes',
  'line-detail': 'Detalle de línea',
  inventory: 'Inventario',
  transfers: 'Traspasos',
  purchases: 'Compras',
  'customer-detail': 'Detalle de cliente',
  'customer-rfm': 'Segmentación de clientes',
  'cash-report': 'Corte de caja',
  'cash-tickets': 'Tickets de caja',
  'store-totals': 'Totales por tienda (móvil)',
  'discr-enrich': 'Discrepancias (sistema)',
  'audit-scan': 'Auditoría de inventario',
  'service-orders': 'Órdenes de taller',
};

export function verbLabel(verb: string | undefined): string {
  if (!verb) return '';
  return VERB_LABELS[verb] ?? verb;
}
